// React hook exposing sync status derived from outbox + sync engine.
import { useCallback, useEffect, useState } from "react";
import * as outbox from "./outbox";
import * as sync from "./sync";
import { getSimulateOffline, isOnline } from "./apiClient";

export function useSyncStatus() {
  const [counts, setCounts] = useState({ pending: 0, in_flight: 0, success: 0, failed_final: 0, total: 0 });
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [simulated, setSimulated] = useState(getSimulateOffline());
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastError, setLastError] = useState(null);

  const refresh = useCallback(async () => {
    setCounts(await outbox.counts());
    setLastSyncAt(await sync.lastSyncAt());
    setOnline(isOnline());
    setSimulated(getSimulateOffline());
  }, []);

  useEffect(() => {
    refresh();
    const unsub = sync.subscribe((ev) => {
      if (ev.type === "drain-start") setSyncing(true);
      if (ev.type === "drain-end") { setSyncing(false); refresh(); }
      if (ev.type === "row-failed-final") setLastError(ev.error);
      if (ev.type === "row-success" || ev.type === "row-backoff") refresh();
    });
    const onOnline = () => refresh();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOnline);
    const iv = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 15000);   // battery
    return () => {
      unsub();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOnline);
      clearInterval(iv);
    };
  }, [refresh]);

  return { counts, syncing, online, simulated, lastSyncAt, lastError, refresh };
}

export function statusLabel({ online, simulated, syncing, counts }) {
  if (simulated) return { kind: "red", text: `Offline (sim) — ${counts.pending} queued` };
  if (!online) return { kind: "red", text: `Offline — ${counts.pending} queued` };
  if (syncing) return { kind: "blue", text: "Syncing…" };
  if (counts.pending > 0) return { kind: "amber", text: `${counts.pending} queued` };
  if (counts.failed_final > 0) return { kind: "amber", text: `${counts.failed_final} failed` };
  if (counts.total === 0 && counts.success === 0) return { kind: "gray", text: "Never synced" };
  return { kind: "green", text: "Online" };
}
